import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import SmartTable from "../../../../components/smart-table/smart-table";
import { Icon, Popup } from "semantic-ui-react";
import AttendeeInCheckpoint from "../../../../components/stats/checkpointStats";
import Status from "../../../../components/ui/status";
import {
	ACTIVE_STATUS,
	ARCHIVE_STATUS,
	INACTIVE_STATUS
} from "../../../../models/checkpoint";
import { Dropdown } from "@weezevent/nacre";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
	PARTICIPATION,
	SCAN,
	SCAN_FAILED
} from "../../../../actions/api/exports/actions";
import { hasCSMWeezard } from "../../../../components/generic/csmWeezard";
import { ROW_SELECTION_PROP_TYPES } from "../../../../components/smart-table/proptypes";
import StopPropagation from "../../../../components/stop-propagation";
import EmptyTable, {
	EmptyTableContext
} from "../../../../components/empty-table";
import useRouterContext from "../../../../context/routerContext";
import Style from "./CheckpointsTable.module.scss";

const CheckpointsTable = ({
	data,
	rowSelection,
	onChangeOrdering,
	ordering,
	canEdit,
	canAdd,
	onDuplicateCheckpoint,
	onChangeCheckpointsStatus,
	onCreateExport,
	onExportAttendeesList,
	pagination,
	loading
}) => {
	const { t } = useTranslation();
	const location = useLocation();

	const history = useHistory();
	const { path } = useRouterContext();

	const editCheckpointLink = checkpoint =>
		`${path}/checkpoints/edit/${checkpoint.id()}${location.search}`;

	const openCheckpointEdit = checkpoint =>
		history.push(editCheckpointLink(checkpoint));

	const columns = [
		{
			key: "id",
			label: t("access.checkpoint.id"),
			render: (_, checkpoint) => checkpoint.id(),
			hide: !hasCSMWeezard()
		},
		{
			key: "checklists",
			label: t("access.checkpoint.checkpointPlural"),
			render: (_, checkpoint) => {
				return (
					<div className={Style.checklistsCell}>
						<Icon
							name="circle"
							size="large"
							className={Style.checklistsCell__colorIcon}
							style={{ color: checkpoint.color() }}
						/>
						<span className={Style.checklistsCell__name}>
							{checkpoint.name()}
						</span>
						{checkpoint.hasIntegrations() ? (
							<Popup
								position="left center"
								content={t(
									"access.checkpoint.actions.integrationText"
								)}
								trigger={<Icon name="clone outline" />}
							/>
						) : null}
					</div>
				);
			}
		},
		{
			key: "attendees",
			label: (
				<div>
					{t("access.checkpoint.attendeeLabel")}
					<br />
					<span>{t("access.checkpoint.scannedExpected")}</span>
				</div>
			),
			render: (_, checkpoint) => (
				<AttendeeInCheckpoint checkpoint={checkpoint} />
			)
		},
		{
			key: "status",
			label: t("access.checkpoint.status"),
			render: (_, checkpoint) => (
				<>
					<Popup
						position="left center"
						content={
							checkpoint.isActive()
								? t("access.checkpoint.actions.onList")
								: t("access.checkpoint.actions.offList")
						}
						trigger={
							<Status
								status={
									checkpoint.isActive()
										? ACTIVE_STATUS
										: INACTIVE_STATUS
								}
							/>
						}
					/>
					{checkpoint.isArchive() ? (
						/* todo why do we display both "archived" and "inactive" ? a checkpoint can't be in both states at once… */
						<Status status={checkpoint.status()} />
					) : null}
				</>
			)
		},
		{
			key: "actions",
			label: t("access.checkpoint.actionsTitle"),
			render: (_, checkpoint) => {
				return (
					<StopPropagation>
						<Dropdown
							className="dropdownitem-middle"
							title={t("access.checkpoint.actionsTitle")}
							size="large"
						>
							<Dropdown.Item
								as={Link}
								to={editCheckpointLink(checkpoint)}
								item={t("access.checkpoint.edit")}
								disabled={!canEdit}
							/>
							<Dropdown.Item
								onClick={() =>
									onDuplicateCheckpoint({ checkpoint })
								}
								item={t("access.common.duplicate")}
								disabled={!canAdd}
							/>
							<Dropdown.Line />
							<Dropdown.Item
								as={Link}
								disabled={
									!checkpoint.getStatistics()?.hasAttendees()
								}
								to={`${path}/checkpoints/${checkpoint.id()}/manual`}
								item={t("access.common.compost")}
							/>
							<Dropdown.Item
								disabled={
									!checkpoint.getStatistics()?.hasAttendees()
								}
								onClick={() => {
									onExportAttendeesList(checkpoint);
								}}
								item={t("access.checkpoint.actions.pdfLabel")}
							/>
							<Dropdown.Line />
							<Dropdown.Item
								disabled={
									!checkpoint.getStatistics()?.hasAttendees()
								}
								onClick={() => {
									onCreateExport([checkpoint], PARTICIPATION);
								}}
								item={t(
									"access.checkpoint.actions.participationLabel"
								)}
							/>
							<Dropdown.Item
								disabled={
									!checkpoint.getStatistics()?.hasScanIn()
								}
								onClick={() => {
									onCreateExport([checkpoint], SCAN);
								}}
								item={t("access.checkpoint.actions.scanLabel")}
							/>
							<Dropdown.Item
								onClick={() => {
									onCreateExport([checkpoint], SCAN_FAILED);
								}}
								item={t(
									"access.checkpoint.actions.failedScanLabel"
								)}
							/>
							<Dropdown.Line />
							{!checkpoint.isArchive() ? (
								<Dropdown.Item
									key="archive"
									disabled={!canEdit}
									onClick={() =>
										onChangeCheckpointsStatus({
											status: ARCHIVE_STATUS,
											checkpoints: [checkpoint]
										})
									}
									item={t(
										"access.checkpoint.actions.archivedLabel"
									)}
								/>
							) : null}
							{checkpoint.isActive() ? (
								<Dropdown.Item
									key="deactive"
									disabled={!canEdit}
									onClick={() =>
										onChangeCheckpointsStatus({
											status: INACTIVE_STATUS,
											checkpoints: [checkpoint]
										})
									}
									item={t(
										"access.checkpoint.actions.inactive"
									)}
								/>
							) : (
								<Dropdown.Item
									key="active"
									onClick={() =>
										onChangeCheckpointsStatus({
											status: ACTIVE_STATUS,
											checkpoints: [checkpoint]
										})
									}
									disabled={!canEdit}
									item={t("access.checkpoint.actions.active")}
								/>
							)}
						</Dropdown>
					</StopPropagation>
				);
			}
		}
	];

	return (
		<SmartTable
			data={data}
			columns={columns.filter(({ hide }) => !hide)}
			rowSelection={rowSelection}
			rowKey={checkpoint => checkpoint.id()}
			onClickSorting={onChangeOrdering}
			currentSorting={ordering}
			onRowClick={openCheckpointEdit}
			customCellClassFn={(checkpoint, column) => {
				if (column !== "actions" && checkpoint.isArchive()) {
					return "disabledRowContent";
				}

				return undefined;
			}}
			loading={loading}
			emptyComponent={
				<EmptyTable
					label="checklist"
					labelPlural="checklists"
					context={EmptyTableContext.SEARCH}
				/>
			}
		>
			<SmartTable.Paginator {...pagination} />
		</SmartTable>
	);
};

CheckpointsTable.propTypes = {
	data: PropTypes.object.isRequired,
	rowSelection: ROW_SELECTION_PROP_TYPES.RowSelection,
	onChangeOrdering: PropTypes.func,
	ordering: PropTypes.object,
	canEdit: PropTypes.bool.isRequired,
	canAdd: PropTypes.bool.isRequired,
	onDuplicateCheckpoint: PropTypes.func,
	onChangeCheckpointsStatus: PropTypes.func,
	onCreateExport: PropTypes.func,
	onExportAttendeesList: PropTypes.func,
	loading: PropTypes.bool,
	pagination: PropTypes.object.isRequired
};

CheckpointsTable.defaultProps = {
	rowSelection: null,
	onChangeOrdering: () => {},
	ordering: null,
	onDuplicateCheckpoint: () => {},
	onChangeCheckpointsStatus: () => {},
	onCreateExport: () => {},
	onExportAttendeesList: () => {},
	loading: false
};

export default CheckpointsTable;
